import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Shirt } from "lucide-react";
import { toast } from "sonner";
import { LINEN_ITEMS } from "@/lib/linenItems";

import { tx, lz } from "@/i18n";
export const LinenDialog = ({ open, onOpenChange, houseId, houseName, houses, reservationId, registration, onSaved }) => {
  const isEdit = !!registration;
  const [vals, setVals] = useState({});
  const [anders, setAnders] = useState("");
  const [andersAantal, setAndersAantal] = useState("");
  const [note, setNote] = useState("");
  const [pickedHouse, setPickedHouse] = useState("");
  const [saving, setSaving] = useState(false);

  const needsHousePicker = !isEdit && !houseId && Array.isArray(houses) && houses.length > 0;
  const effectiveHouseId = isEdit ? registration.house_id : (houseId || pickedHouse);

  useEffect(() => {
    if (open && registration) {
      const v = {};
      for (const { key } of LINEN_ITEMS) if (registration.items?.[key] > 0) v[key] = String(registration.items[key]);
      setVals(v);
      setAnders(registration.anders || "");
      setAndersAantal(registration.anders_aantal ? String(registration.anders_aantal) : "");
      setNote(registration.note || "");
    }
  }, [open, registration]);

  const set = (k) => (e) => setVals((p) => ({ ...p, [k]: e.target.value }));
  const reset = () => { setVals({}); setAnders(""); setAndersAantal(""); setNote(""); setPickedHouse(""); };

  const save = async () => {
    if (!effectiveHouseId) { toast.error(tx("Kies eerst een woning")); return; }
    setSaving(true);
    try {
      const items = {};
      for (const { key } of LINEN_ITEMS) {
        const n = parseInt(vals[key], 10);
        if (n > 0) items[key] = n;
      }
      const payload = {
        house_id: effectiveHouseId,
        reservation_id: isEdit ? registration.reservation_id : (reservationId || null),
        items, anders, anders_aantal: andersAantal ? parseInt(andersAantal, 10) : null, note,
      };
      if (isEdit) {
        await api.put(`/linen/${registration.id}`, payload);
        toast.success(tx("Registratie bijgewerkt"));
      } else {
        await api.post("/linen", payload);
        toast.success(tx("Linnen geregistreerd"));
      }
      reset();
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? (registration.house_name || tx("Woning")) : (houseName || (houses?.find((h) => h.id === pickedHouse)?.name) || tx("Woning"));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="linen-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Shirt className="h-5 w-5 text-secondary" /> {isEdit ? tx("Linnen bewerken") : tx("Linnen registreren")}</DialogTitle>
          <DialogDescription>{title} {tx("— vul de aantallen in (leeg = 0).")}</DialogDescription>
        </DialogHeader>
        {needsHousePicker && (
          <div>
            <Label className="text-xs">{tx("Woning")}</Label>
            <Select value={pickedHouse} onValueChange={setPickedHouse}>
              <SelectTrigger className="mt-1" data-testid="linen-dialog-house-select"><SelectValue placeholder={tx("Kies een woning")} /></SelectTrigger>
              <SelectContent>
                {houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {LINEN_ITEMS.map(({ key, label }) => (
            <div key={key}>
              <Label className="text-xs">{label}</Label>
              <Input type="number" min="0" value={vals[key] || ""} onChange={set(key)} placeholder="0" className="mt-1" data-testid={`linen-input-${key}`} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><Label className="text-xs">{tx("Anders")}</Label><Input value={anders} onChange={(e) => setAnders(e.target.value)} placeholder={tx("Vrij veld — bijv. losse items")} className="mt-1" data-testid="linen-input-anders" /></div>
          <div><Label className="text-xs">{tx("Aantal")}</Label><Input type="number" min="0" value={andersAantal} onChange={(e) => setAndersAantal(e.target.value)} placeholder="0" className="mt-1" data-testid="linen-input-anders-aantal" /></div>
        </div>
        <div><Label className="text-xs">{tx("Opmerking")}</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" data-testid="linen-input-note" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="linen-cancel">{tx("Annuleren")}</Button>
          <Button onClick={save} disabled={saving || !effectiveHouseId} className="bg-primary hover:bg-primary/90" data-testid="linen-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Opslaan")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
